import React, { useEffect, useMemo, useState } from "react";
import { User } from "../../../../types/user";
import {
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchUser,
  fetchUserCase,
} from "../../../../services/httpClient";
import { useParams } from "react-router-dom";
import ToastMsg from "../../../ui/ToastMsg";
import PageTitle from "../../../ui/PageTitle";
import { Button, Card, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import HouseholdCard from "./HouseholdCard";
import EditMember from "./EditMember";
import SaveChangesModal from "../../../ui/SaveChangesModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import ViewMember from "./ViewMember";
import CreateMember from "./CreateMember";
import CaseHousing from "./CaseHousing";
import { personHousing } from "../../../../helpers/housingAssignment";
import useApp from "../../../../utils/useApp";
import useHousingInventory from "../../../../utils/useHousingInventory";

interface HouseholdState {
  myCase: boolean;
  userToSave: boolean;
  setUserToSave: any;
  showModal: boolean;
  setShowModal: any;
  family: any;
  getFamily: any;
}

const Household = ({ myCase, userToSave, setUserToSave, showModal, setShowModal, family, getFamily }: HouseholdState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [currentUser, setCurrentUser] = useState<User>();
  const [currentCase, setCurrentCase] = useState<any>();
  const [relationships, setRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const inventory = useHousingInventory();
  const { rooms, beds, dwellings, facilities } = inventory;
  const [create, setCreate] = useState<boolean>(false);
  const [editMode, setEditMode] = useState(false);
  const [showAdults, setShowAdults] = useState<boolean>(false);
  const [showChildren, setShowChildren] = useState<boolean>(false);
  const [showBanned, setShowBanned] = useState<boolean>(false);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [housingOpen, setHousingOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");

  const active = useMemo(() => family.filter((f: any) => !f.is_banned && !f.deleted_at), [family]);

  //family organized to display
  //1
  const peopleServedAndPrimary = useMemo(() => active.filter((f: any) => f.is_case_subject && f.is_primary), [active]);
  //2
  const peopleServed = useMemo(() => active.filter((f: any) => f.is_case_subject && !f.is_primary), [active]);
  //3
  const primaryContact = useMemo(() => active.filter((f: any) => !f.is_case_subject && f.is_primary), [active]);
  //exclude the people served and primary contacts from groupings below
  //4
  const primaryAdults = useMemo(() => active.filter((f: any) => f.role === "family" && !f.is_primary && !f.is_case_subject), [active]);
  const members = useMemo(() => active.filter((f: any) => f.role !== "family" && !f.is_case_subject), [active]);
  //5
  const otherAdults = useMemo(() => members.filter((m: any) => m.relationship !== "child"), [members]);
  //6
  const children = useMemo(() => members.filter((m: any) => m.relationship === "child"), [members]);
  //should count include banned
  const householdCount = useMemo(() => family.filter((f: any) => !f.deleted_at).length, [family]);

  //pull out deleted and banned
  const banned = useMemo(() => family.filter((f: any) => f.is_banned && !f.deleted_at), [family]);
  const deleted = useMemo(() => family.filter((f: any) => f.deleted_at), [family]);

  const getCase = () => {
    fetchUserCase(id).then((res: any) => {
      setCurrentCase(res[0].data);
    });
  };

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setRelationships(res[0].data);
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms().then((res: any) => {
      setPrograms(res[0].data);
    });
  };

  const getJobs = async () => {
    fetchJobs().then((res: any) => {
      setJobs(res[0].data);
    });
  };

  useEffect(() => {
    if (family) {
      if (family?.length > 0) {
        if (currentUser) {
          const updatedUser = family.find((f: any) => f.id === currentUser.id);
          setCurrentUser(updatedUser);
          return;
        }
        if (peopleServedAndPrimary?.length > 0) {
          setCurrentUser(peopleServedAndPrimary[0]);
        } else if (peopleServed?.length > 0) {
          setCurrentUser(peopleServed[0]);
        } else if (primaryContact?.length > 0) {
          setCurrentUser(primaryContact[0]);
        } else if (primaryAdults?.length > 0) {
          setCurrentUser(primaryAdults[0]);
          setShowAdults(true);
        } else if (otherAdults?.length > 0) {
          setCurrentUser(otherAdults[0]);
          setShowAdults(true);
        } else {
          setCurrentUser(children[0]);
          setShowChildren(true);
        }
      }
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [family]);

  useEffect(() => {
    getCase();
    getRelationships();
    getGenders();
    getEthnicities();
    getRaces();
    getPrograms();
    getJobs();
  }, []);

  //if no name for a CM replace with email
  useEffect(() => {
    if (currentCase?.primaryCM?.user_name === " ") {
      fetchUser(currentCase.primaryCM.user_id)
        .then((res: any) => {
          setCurrentCase((prev: any) => ({
            ...prev,
            primaryCM: {
              ...prev.primaryCM,
              user_name: res[0].data.email,
            },
          }));
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
    if (currentCase?.secondaryCM?.user_name === " ") {
      fetchUser(currentCase.secondaryCM.user_id)
        .then((res: any) => {
          setCurrentCase((prev: any) => ({
            ...prev,
            secondaryCM: {
              ...prev.secondaryCM,
              user_name: res[0].data.email,
            },
          }));
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [currentCase]);

  const onLeave = async () => {
    setIsSaving(true);
    await getFamily();
    resetHouseholdTab();
    setIsSaving(false);
  };

  const onClickDoneEdit = (forceNoModal: boolean = false) => {
    if (!forceNoModal && userToSave) {
      setShowModal(true);
    } else {
      onLeave();
    }
  };

  const showCreate = (e: any) => {
    if (userToSave) {
      setShowModal(true);
    } else {
      setCreate(true);
      setCurrentUser(undefined);
    }
  };

  // where the selected person sleeps, worked out once for both the view and edit panes
  const housing =
    currentUser && (dwellings.length > 0 || rooms.length > 0)
      ? personHousing({ caseId: id, person: currentUser, dwellings, rooms, facilities, beds })
      : { kind: "" as const, place: "", label: "" };

  // The person panes speak about the case's dwelling or room only; an individual bed is
  // managed in the Housing card and isn't surfaced on the person.
  const caseLevelHousing = housing.kind === "dwelling" || housing.kind === "room" ? housing : { kind: "" as const, place: "", label: "" };

  const resetHouseholdTab = () => {
    setCreate(false);
    setEditMode(false);
    setUserToSave(false);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          {!housingOpen &&
            (myCase && create ? (
              <PageTitle title="Create Household Member" buttonTitle="Cancel" onButtonClick={() => onLeave()} type="section" />
            ) : myCase && !create ? (
              <PageTitle title="Household" buttonTitle="Add Member" onButtonClick={() => showCreate(true)} type="section" />
            ) : (
              <PageTitle title="Household" type="section" />
            ))}

          {!create && auth?.profile?.includeHMIS === 1 && (
            <CaseHousing
              caseId={id}
              caseName={currentCase?.name || "this case"}
              caseLevelHousing={caseLevelHousing.label}
              people={active}
              dwellings={dwellings}
              facilities={facilities}
              rooms={rooms}
              beds={beds}
              myCase={myCase}
              isOpen={housingOpen}
              setIsOpen={setHousingOpen}
              onChanged={() => {
                inventory.refresh();
                getFamily();
              }}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          )}

          <Row className={housingOpen ? "d-none" : "flex-column flex-lg-row"}>
            <Col xs={12} lg={4} xl={3} className="household-sidebar mb-3 z-0">
              <div className="d-flex flex-lg-column flex-row overflow-auto gap-2 pb-2">
                <Card className="mb-lg-1 flex-shrink-0">
                  <Card.Body>
                    <div className="d-flex flex-lg-column flex-row gap-2 pb-1 pb-lg-0">
                      {peopleServedAndPrimary?.length > 0 && (
                        <>
                          {peopleServedAndPrimary.map((u: User) => (
                            <div key={u.id} className="flex-shrink-0">
                              <HouseholdCard
                                user={u}
                                currentUser={currentUser}
                                setCurrentUser={setCurrentUser}
                                userToSave={userToSave}
                                setShowModal={setShowModal}
                                createMode={create}
                              />
                            </div>
                          ))}
                        </>
                      )}
                      {peopleServed?.length > 0 && (
                        <>
                          {peopleServed.map((u: User) => (
                            <div key={u.id} className="flex-shrink-0">
                              <HouseholdCard
                                user={u}
                                currentUser={currentUser}
                                setCurrentUser={setCurrentUser}
                                userToSave={userToSave}
                                setShowModal={setShowModal}
                                createMode={create}
                              />
                            </div>
                          ))}
                        </>
                      )}
                      {primaryContact?.length > 0 && (
                        <>
                          {primaryContact.map((u: User) => (
                            <div key={u.id} className="flex-shrink-0" style={{ minWidth: "180px" }}>
                              <HouseholdCard
                                user={u}
                                currentUser={currentUser}
                                setCurrentUser={setCurrentUser}
                                userToSave={userToSave}
                                setShowModal={setShowModal}
                                createMode={create}
                              />
                            </div>
                          ))}
                        </>
                      )}
                      {(primaryAdults?.length > 0 || otherAdults?.length > 0) && (
                        <h6 className="app-header-text cursor-pointer mb-0 mt-1" onClick={() => setShowAdults(!showAdults)}>
                          Adults <FontAwesomeIcon icon={!showAdults ? faAngleRight : faAngleDown} />
                        </h6>
                      )}
                      {(showAdults || window.innerWidth < 992) &&
                        [...primaryAdults, ...otherAdults].map((u: User) => (
                          <div key={u.id} className="flex-shrink-0" style={{ minWidth: "180px" }}>
                            <HouseholdCard
                              user={u}
                              currentUser={currentUser}
                              setCurrentUser={setCurrentUser}
                              userToSave={userToSave}
                              setShowModal={setShowModal}
                              createMode={create}
                            />
                          </div>
                        ))}
                      {children?.length > 0 && (
                        <h6 className="app-header-text cursor-pointer mb-0 mt-1" onClick={() => setShowChildren(!showChildren)}>
                          Children <FontAwesomeIcon icon={!showChildren ? faAngleRight : faAngleDown} />
                        </h6>
                      )}
                      {(showChildren || window.innerWidth < 992) &&
                        children.map((u: User) => (
                          <div key={u.id} className="flex-shrink-0" style={{ minWidth: "180px" }}>
                            <HouseholdCard
                              user={u}
                              currentUser={currentUser}
                              setCurrentUser={setCurrentUser}
                              userToSave={userToSave}
                              setShowModal={setShowModal}
                              createMode={create}
                            />
                          </div>
                        ))}

                      {banned?.length > 0 && (
                        <h6 className="app-header-text cursor-pointer mb-0 mt-1" onClick={() => setShowBanned(!showBanned)}>
                          Banned <FontAwesomeIcon icon={!showBanned ? faAngleRight : faAngleDown} />
                        </h6>
                      )}
                      {(showBanned || window.innerWidth < 992) &&
                        banned.map((u: User) => (
                          <div key={u.id} className="flex-shrink-0" style={{ minWidth: "180px" }}>
                            <HouseholdCard
                              user={u}
                              currentUser={currentUser}
                              setCurrentUser={setCurrentUser}
                              userToSave={userToSave}
                              setShowModal={setShowModal}
                              createMode={create}
                            />
                          </div>
                        ))}
                      {deleted?.length > 0 && (
                        <h6 className="app-header-text cursor-pointer mb-0 mt-1" onClick={() => setShowDeleted(!showDeleted)}>
                          Deleted <FontAwesomeIcon icon={!showDeleted ? faAngleRight : faAngleDown} />
                        </h6>
                      )}
                      {(showDeleted || window.innerWidth < 992) &&
                        deleted?.map((u: User) => (
                          <div key={u.id} className="flex-shrink-0" style={{ minWidth: "180px" }}>
                            <HouseholdCard
                              user={u}
                              currentUser={currentUser}
                              setCurrentUser={setCurrentUser}
                              userToSave={userToSave}
                              setShowModal={setShowModal}
                              createMode={create}
                            />
                          </div>
                        ))}
                    </div>
                  </Card.Body>
                </Card>
                <Card className="mb-lg-1 flex-shrink-0" style={{ minWidth: "200px" }}>
                  <Card.Body>
                    <h6 className="app-header-text">Case Manager(s)</h6>
                    <Stack>
                      {currentCase?.primaryCM && (
                        <>
                          <small className="app-blue-text">{currentCase?.primaryCM?.user_name}</small>
                        </>
                      )}
                      {currentCase?.secondaryCM && (
                        <>
                          <small className="app-blue-text">{currentCase?.secondaryCM?.user_name}</small>
                        </>
                      )}
                    </Stack>
                  </Card.Body>
                </Card>
                <Card className="mb-lg-1 flex-shrink-0" style={{ minWidth: "200px" }}>
                  <Card.Body>
                    <h6 className="app-header-text">Address</h6>
                    {currentCase?.address_line_one ? (
                      <small className="app-blue-text">
                        {currentCase?.address_line_one && currentCase?.address_line_one}
                        {currentCase?.address_line_one && <br />}
                        {currentCase?.address_line_two && currentCase?.address_line_two}
                        {currentCase?.address_line_two && <br />}
                        {currentCase?.city && currentCase?.state && currentCase?.city + ", " + currentCase?.state + "  " + currentCase?.zip}
                      </small>
                    ) : (
                      <small className="app-blue-text">No Address Saved</small>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </Col>
            <Col xs={12} lg={8} xl={9} className="ps-3">
              {isSaving ? (
                <Container>
                  <Button size="sm" className="spinner-button spinner-button-main">
                    <Spinner animation="border" style={{ color: "#F6893D" }} />
                  </Button>
                </Container>
              ) : create ? (
                <CreateMember
                  originalRooms={rooms}
                  originalBeds={beds}
                  possiblePrimaryContacts={[...peopleServedAndPrimary, ...peopleServed, ...primaryContact, ...primaryAdults]}
                  currentCase={currentCase}
                  relationships={relationships}
                  genders={genders}
                  ethnicities={ethnicities}
                  races={races}
                  originalPrograms={programs}
                  myCase={myCase}
                  setUserToSave={setUserToSave}
                  getFamily={getFamily}
                  onClickDoneEdit={onClickDoneEdit}
                  setIsSaving={setIsSaving}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              ) : editMode ? (
                <EditMember
                  user={currentUser}
                  housing={caseLevelHousing}
                  possiblePrimaryContacts={[...peopleServedAndPrimary, ...peopleServed, ...primaryContact, ...primaryAdults]}
                  currentCase={currentCase}
                  relationships={relationships}
                  genders={genders}
                  ethnicities={ethnicities}
                  races={races}
                  rooms={rooms}
                  originalPrograms={programs}
                  originalJobs={jobs}
                  myCase={myCase}
                  setUserToSave={setUserToSave}
                  onClickDoneEdit={onClickDoneEdit}
                  setIsSaving={setIsSaving}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              ) : (
                currentUser && (
                  <ViewMember
                    user={currentUser}
                    housingLabel={caseLevelHousing.label}
                    activeFamily={active}
                    fetchHousehold={getFamily}
                    currentCase={currentCase}
                    relationships={relationships}
                    genders={genders}
                    ethnicities={ethnicities}
                    races={races}
                    programs={programs}
                    jobs={jobs}
                    rooms={rooms}
                    myCase={myCase}
                    isLastUser={householdCount === 1}
                    resetHouseholdTab={resetHouseholdTab}
                    setEditMode={setEditMode}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                  />
                )
              )}
            </Col>
          </Row>
        </>
      )}

      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={() => onLeave()} />
    </>
  );
};

export default Household;
